/* eslint-disable no-unused-vars */
import React, { useRef, useState } from "react";
import { arrayOf, node, shape, string, object, number, oneOf, bool } from "prop-types";
import classNames from "classnames/bind";
import { Pagination, Scrollbar, A11y } from "swiper/modules";
import { Swiper, SwiperSlide } from "../SwiperSlider/Slider";
import "swiper/css";
import { SliderPagination } from "./Pagination/SliderPagination";
import { SliderNavigation } from "./Navigation/SliderNavigation";

import * as styles from "./Slider.module.scss";

const cx = classNames.bind(styles);

const Slider = ({
	slides,
	a11y,
	id,
	slideClass,
	paginationClasses,
	navigationClasses,
	moreLink,
	leftNavigation,
	rightNavigation,
	breakpoints,
	slidesPerPage,
	paging,
	SliderInViewPort,
	PaginationCap,
	isSwiperReady,
}) => {
	const [isSwiperLoaded, setIsSwiperLoaded] = useState(false);
	const swiperRef = useRef();
	const slidesCount = slides?.length ?? 0;

	if (slidesCount === 0) {
		return null;
	}

	if (slidesCount === 1) {
		const slide = slides[0];
		return <div className={classNames(slideClass)}>{slide}</div>;
	}

	return (
		<div
			className={cx(styles.slider, {
				[styles.sliderSwiperReady]: isSwiperReady,
			})}>
			{leftNavigation && rightNavigation && (
				<SliderNavigation
					slot="container-start"
					swiperRefLoaded={isSwiperLoaded}
					swiperRef={swiperRef}
					totalSlides={slides.length}
					navigationClasses={navigationClasses}
					leftNavigation={leftNavigation}
					slidesPerPage={Math.floor(SliderInViewPort)}
					rightNavigation={rightNavigation}
					id={id}
				/>
			)}

			<Swiper
				modules={[Pagination, Scrollbar, A11y]}
				onSwiper={swiper => {
					swiperRef.current = swiper;
					setIsSwiperLoaded(true);
				}}
				pagination={
					PaginationCap && {
						type: "bullets",
						clickable: true,
						bulletClass: cx(styles.bullet),
						dynamicBullets: true,
						bulletActiveClass: cx(styles.bulletActive),
					}
				}
				navigation={{
					nextEl: `.${navigationClasses.nextButton}`,
					prevEl: `.${navigationClasses.prevButton}`,
				}}
				scrollbar={{ draggable: true }}
				a11y={a11y}
				slidesPerView="auto"
				spaceBetween={2}
				breakpoints={breakpoints}
				watchSlidesVisibility={true}
				watchSlidesProgress={true}
				watchOverflow={true}
				className={cx({ swiperBottomPadding: PaginationCap })}>
				{slides.map(slide => (
					<SwiperSlide key={slide.id} tag="li" className={slideClass}>
						{slide.view}
					</SwiperSlide>
				))}
			</Swiper>

			{paging && !PaginationCap && (
				<SliderPagination
					slot="container-end"
					swiperRefLoaded={isSwiperLoaded}
					swiperRef={swiperRef}
					totalSlides={slides.length}
					paginationClasses={paginationClasses}
					paginationBulletMessage={a11y.paginationBulletLabel}
					startSliderMessage={a11y.startSliderMessage}
					stopSliderMessage={a11y.stopSliderMessage}
					moreLink={moreLink}
					slidesPerPage={Math.floor(SliderInViewPort)}
				/>
			)}
		</div>
	);
};

Slider.propTypes = {
	slides: arrayOf(object).isRequired,
	/**
	 *  Used for aria labels - please provide ALL labels
	 */
	a11y: shape({
		prevSlideLabel: string,
		nextSlideLabel: string,
		firstSlideLabel: string,
		lastSlideLabel: string,
		paginationBulletLabel: string,
		stopSliderLabel: string,
		startSliderLabel: string,
	}),
	slideClass: string,
	paginationClasses: shape({
		pagination: string,
		paginationItem: string,
		bullet: string,
		bulletActive: string,
		autoplay: string,
	}),
	navigationClasses: shape({
		button: string,
		prevButton: string,
		nextButton: string,
	}),
	moreLink: node,
	// TODO: leftNavigation / rightNavigation should be boolean based on current use case, but implementation should use node so that custom icon / element can be used
	leftNavigation: node,
	rightNavigation: node,
	breakpoints: object,
	slidesPerPage: number,
	SliderInViewPort: number,
	paging: bool,
	id: string,
	PaginationCap: bool,
	isSwiperReady: bool,
};

Slider.defaultProps = {
	a11y: {
		prevSlideMessage: "Previous slide",
		nextSlideMessage: "Next slide",
		firstSlideMessage: "This is the first slide",
		lastSlideMessage: "This is the last slide",
		paginationBulletLabel: "Go to slide {{index}}",
	},
	slideClass: null,
	paginationClasses: {},
	navigationClasses: {},
	moreLink: null,
	leftNavigation: null,
	rightNavigation: null,
	breakpoints: null,
	slidesPerPage: 0,
	SliderInViewPort: 0,
	paging: true,
	id: null,
	PaginationCap: false,
	isSwiperReady: false,
};
export default Slider;
export { Slider };
